import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InputMask from "react-input-mask";
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import * as Yup from "yup";
import { debounce } from "lodash";
import { formatToReadableDate, formatToISODate, parseReadableDate } from "@/utils/formatDate";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { countryList } from "@/utils/countryList";
import AutoCompleteLoader from "@/components/Application/AutoCompleteLoader";
import LicencePassportToggle from "./LicencePassportToggle";
import { saveCustomerProfile, updateCustomer, updateCustomerData } from "../../store/slices/applicationFormSlice";
import regex from "../../utils/regex";
import {
  ID_TYPE,
  MARITAL_STATUSES,
  customerTitleList,
  fieldRequiredMessage,
  isVisaResidencyStatus,
  residencyStatusOptions,
  VISA_RESIDENCY_STATUS,
  stateList,
  validCustomerTitle,
  visaOptionsByResidencyStatus,
} from "../../constants";
import RadioButtonGroup from "../Utils/RadioButtonGroup";
import { customerSelector } from "../../store/slices/customerSlice";

const dateFieldNames = new Set(["dateOfBirth", "IDExpiryDate", "visaExpiryDate"]);

const buildReadableDateFieldSchema = () =>
  Yup.string()
    .typeError("Please enter a valid date")
    .matches(regex.customerDateOfBirthRegexOptional, "Invalid date, please enter a valid date");

const buildExpiryDateFieldSchema = () =>
  buildReadableDateFieldSchema().test("not-in-past", "Expiry date cannot be in the past", (value) => {
    if (!value) return true;
    const parsed = parseReadableDate(value);
    if (!parsed) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parsed.getTime() >= today.getTime();
  });

const formatFieldValueForSave = (fieldName, value) =>
  dateFieldNames.has(fieldName) ? formatToISODate(value) : value;

const getCustomerProfileFieldKey = (fieldName, customerId) => `customerProfile.${fieldName}_${customerId}`;

const buildPersonalDetailsValues = (customer) => ({
  customerTitle: customer?.customerTitle || "",
  firstName: customer?.firstName || "",
  middleNames: customer?.middleNames || "",
  lastName: customer?.lastName || "",
  IDNumber: customer?.IDNumber || "",
  IDCardNumber: customer?.IDCardNumber || "",
  dateOfBirth: formatToReadableDate(customer?.dateOfBirth || ""),
  residencyStatusValue: customer?.customerProfile?.residencyStatus,
  visaType: customer?.customerProfile?.visaType || "",
  visaExpiryDate: formatToReadableDate(customer?.customerProfile?.visaExpiryDate || ""),
  maritalStatusValue: customer?.customerProfile?.maritalStatus,
  IDExpiryDate: formatToReadableDate(customer?.IDExpiryDate || ""),
  IDType: customer?.IDType || ID_TYPE.LICENCE,
  IDState: customer?.IDState || "",
  IDCountry: customer?.IDCountry || "",
});

const CustomerPersonalDetails = React.memo(
  ({ customer, customerId, applicationId, fieldErrors = {}, setFieldErrors, isDisableEdit }) => {
    const dispatch = useDispatch();
    const { isCustomerLogin } = useSelector(customerSelector);
    const isDisable = isCustomerLogin ? false : isDisableEdit;
    const currentCustomerId = customerId || customer?._id;

    const [allValues, setAllValue] = useState(() => buildPersonalDetailsValues(customer));

    useEffect(() => {
      if (customer) {
        setAllValue(buildPersonalDetailsValues(customer))
      }
    }, [customer])

    const buildIDNumberFieldSchema = (idType) =>
      Yup.string()
        .optional()
        .matches(regex.drivingPassportRegex, "Only alphanumeric and - characters allowed.")
        .min(6, "Minimum of 6 characters")
        .max(
          idType === ID_TYPE.LICENCE ? 10 : 20,
          idType === ID_TYPE.LICENCE ? "Maximum of 10 characters" : "Maximum of 20 characters",
        );

    const validationSchema = Yup.object({
      customerTitle: Yup.string().oneOf(validCustomerTitle, "Please select title").required("Title is required"),
      firstName: Yup.string()
        .required(fieldRequiredMessage.firstName)
        .matches(regex.customerNameRegex, "Only letters and ' - characters allowed.")
        .min(2, "Minimum of 2 characters")
        .max(50, "Maximum of 50 characters"),
      middleNames: Yup.string()
        .optional()
        .matches(regex.customerNameRegex, "Only letters and ' - characters allowed.")
        .max(50, "Maximum of 50 characters"),
      lastName: Yup.string()
        .required(fieldRequiredMessage.lastName)
        .matches(regex.customerNameRegex, "Only letters and ' - characters allowed.")
        .min(2, "Minimum of 2 characters")
        .max(50, "Maximum of 50 characters"),
      IDNumber: buildIDNumberFieldSchema(allValues?.IDType),
      IDState: Yup.string().optional(),
      IDCountry: Yup.string().optional(),
      dateOfBirth: buildReadableDateFieldSchema()
        .test("min-age-18", "Customer must be at least 18 years old", (value) => {
          if (!value) return true;
          const parsed = parseReadableDate(value);
          if (!parsed) return true;
          const eighteenYearsAgo = new Date();
          eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
          return parsed.getTime() <= eighteenYearsAgo.getTime();
        }),
      IDCardNumber: buildIDNumberFieldSchema(allValues?.IDType),
      IDExpiryDate: buildExpiryDateFieldSchema(),
      visaExpiryDate: buildExpiryDateFieldSchema(),
    });

    const debouncedValidation = useCallback(
      debounce(async (name, fieldName, value) => {
        try {
          await validationSchema.validateAt(name, { [name]: value });
          setFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
        } catch (error) {
          setFieldErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: error.message,
          }));
        }
      }, 300),
      []
    );

    const handle = {
      onChangeField: (e, nameField) => {
        let isValid = true;
        const name = nameField.split("_")[0];
        if (name === "firstName" || name === "middleNames" || name === "lastName") {
          isValid = regex.customerNameRegex.test(e) && e?.length <= 50; // nameRegex
        }
        if (!isValid) {
          debouncedValidation(name, nameField, e)
          return;
        }

        setAllValue({ ...allValues, [name]: e });
        debouncedValidation(name, nameField, e)
      },
      onChangeRadio: (value, name) => {
        setAllValue({ ...allValues, [name]: value });
      },
      onChangeCustomerProfileField: (value, name) => {
        setAllValue({ ...allValues, [name]: value });
        debouncedValidation(name, getCustomerProfileFieldKey(name, currentCustomerId), value);
      },
      handleSaveCustomer: (data) => {
        dispatch(
          updateCustomer({
            data: {
              customerId: customer._id,
              data: {
                application: applicationId,
                ...data,
              },
            },
          }),
        );
      },
      handleSaveCustomerProfile: (data) => {
        dispatch(
          saveCustomerProfile({
            data: {
              ...data,
              customer: customer._id,
              applicationId,
            },
          }),
        );
      },
      handleResidencyStatus: (value, profileData = {}) => {
        const oldVal = customer?.customerProfile?.residencyStatus;
        const hasProfileUpdates = Object.entries(profileData).some(
          ([fieldName, fieldValue]) => (customer?.customerProfile?.[fieldName] || "") !== fieldValue,
        );
        if (oldVal === value && !hasProfileUpdates) {
          return;
        }
        handle.handleSaveCustomerProfile({
          residencyStatus: value,
          ...profileData,
        });
      },
      handleResidencyStatusChange: (value) => {
        const shouldClearVisaFields =
          allValues?.residencyStatusValue !== value &&
          (isVisaResidencyStatus(allValues?.residencyStatusValue) || isVisaResidencyStatus(value));
        const clearedVisaFields = shouldClearVisaFields
          ? {
              visaType: "",
              visaExpiryDate: "",
            }
          : {};

        setAllValue({
          ...allValues,
          residencyStatusValue: value,
          ...clearedVisaFields,
        });

        if (shouldClearVisaFields) {
          setFieldErrors((prevErrors) => ({
            ...prevErrors,
            [getCustomerProfileFieldKey("visaType", currentCustomerId)]: "",
            [getCustomerProfileFieldKey("visaExpiryDate", currentCustomerId)]: "",
          }));
        }

        handle.handleResidencyStatus(value, clearedVisaFields);
      },
      handleMaritalStatus: (value) => {
        let oldVal = customer?.customerProfile?.maritalStatus;
        if (oldVal === value) {
          return;
        }
        handle.handleSaveCustomerProfile({
          maritalStatus: value,
        });
      },
      handleVisaType: (value) => {
        const nextValue = value || "";
        setAllValue({ ...allValues, visaType: nextValue });
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [getCustomerProfileFieldKey("visaType", currentCustomerId)]: "",
        }));
        if ((customer?.customerProfile?.visaType || "") === nextValue) {
          return;
        }
        handle.handleSaveCustomerProfile({
          visaType: nextValue,
        });
      },
      handleLicenceNumber: () =>
        customer
          ? customer?.documents?.find((doc) => (doc.details?.licence_number ? doc.details?.licence_number : ""))
            ?.details?.licence_number
          : "",
      handleSaveIDType: (value) => {
        handle.handleSaveCustomer({
          IDType: value,
        });
        dispatch(
          updateCustomerData({
            customerId: customer._id,
            key: 'IDType',
            value,
          }),
        );

        // Re-validate IDNumber/IDCardNumber against the NEW IDType limits.
        // The regular validation closures still hold the old IDType at this point,
        // so build a fresh schema from `value` (the new IDType, known synchronously).
        const freshSchema = buildIDNumberFieldSchema(value);
        const revalidate = (fieldKey, fieldValue) => {
          if (!fieldValue) {
            setFieldErrors((prev) => ({ ...prev, [fieldKey]: "" }));
            return;
          }
          try {
            freshSchema.validateSync(fieldValue);
            setFieldErrors((prev) => ({ ...prev, [fieldKey]: "" }));
          } catch (error) {
            setFieldErrors((prev) => ({ ...prev, [fieldKey]: error.message }));
          }
        };
        revalidate(`IDNumber_${customer._id}`, allValues.IDNumber);
        revalidate(`IDCardNumber_${customer._id}`, allValues.IDCardNumber);
      },
       blurFn: (field, value) => {
        const fieldName = field.split("_")[0];
        let fieldErr = "";
        try {
          validationSchema.validateSyncAt(fieldName, { [fieldName]: value });
        } catch (error) {
          fieldErr = error.message;
        }

        setFieldErrors((pre) => ({
          ...pre,
          [field]: fieldErr,
        }));

        if (fieldErr === "") {
          let oldVal = customer[field];
          if (oldVal !== value) {
            const formattedValue = formatFieldValueForSave(fieldName, value);
            handle.handleSaveCustomer({
              [fieldName]: formattedValue,
            });

            dispatch(
              updateCustomerData({
                customerId: customer._id,
                key: fieldName,
                value: formattedValue,
              }),
            );
          }
        }
      },
      blurCustomerProfileFn: (fieldName, value) => {
        const fieldKey = getCustomerProfileFieldKey(fieldName, currentCustomerId);
        let fieldErr = "";
        try {
          validationSchema.validateSyncAt(fieldName, { [fieldName]: value });
        } catch (error) {
          fieldErr = error.message;
        }

        setFieldErrors((pre) => ({
          ...pre,
          [fieldKey]: fieldErr,
        }));

        if (fieldErr === "") {
          const formattedValue = formatFieldValueForSave(fieldName, value);
          if ((customer?.customerProfile?.[fieldName] || "") !== formattedValue) {
            handle.handleSaveCustomerProfile({
              [fieldName]: formattedValue,
            });
          }
        }
      },
    };

    const isLargeScreen = useMediaQuery("(min-width: 768px)");

    const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
    const getLocalAndLenderError = ({ fieldName, localFieldName }) => {
      const fieldError = fieldErrors[fieldName];
      return getCustomerOrEntityLocalAndLenderError({ fieldError, localState: allValues, fieldName, localFieldName })
    };
    const showVisaFields =
      isVisaResidencyStatus(allValues?.residencyStatusValue) &&
      allValues?.residencyStatusValue !== VISA_RESIDENCY_STATUS.STUDENT;
    const selectedVisaOptions = visaOptionsByResidencyStatus[allValues?.residencyStatusValue] || [];
    const selectedVisaType = selectedVisaOptions.includes(allValues?.visaType) ? allValues?.visaType : null;
    const visaTypeError = getLocalAndLenderError({
      fieldName: getCustomerProfileFieldKey("visaType", currentCustomerId),
      localFieldName: "visaType",
    });
    const visaExpiryDateError = getLocalAndLenderError({
      fieldName: getCustomerProfileFieldKey("visaExpiryDate", currentCustomerId),
      localFieldName: "visaExpiryDate",
    });

    return (
      <React.Fragment>
        <Grid
          id={`customer-${customer?._id}`}
          style={{
            margin: "0 0 30px",
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            paddingBottom: "30px",
          }}
        >
          <Grid item md={12} sm={12} style={{ margin: "0 0 10px" }}>
            <Typography id="input-slider" fontWeight={600}>
              Personal details
            </Typography>
          </Grid>
          <Grid container item xs={12}>
            <Stack spacing={1} direction={{ xs: "column", sm: "row" }} margin={"0 0 10px"} width={"100%"}>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth variant="filled">
                  <InputLabel id={`customerTitle_${customer._id}`} error={!!getLocalAndLenderError({ fieldName: `customerTitle_${customer._id}` })}>Title* </InputLabel>
                  <Select
                    size="small"
                    labelId={`customerTitle_${customer._id}`}
                    id={`customerTitle_${customer._id}`}
                    value={allValues?.customerTitle}
                    label="Title"
                    name={`customerTitle_${customer._id}`}
                    disabled={isDisable}
                    onChange={(event) => {
                      handle.onChangeField(event?.target?.value, `customerTitle_${customer._id}`);
                    }}
                    onBlur={(event) => handle.blurFn(`customerTitle_${customer._id}`, event.target.value)}
                    error={!!getLocalAndLenderError({ fieldName: `customerTitle_${customer._id}` })}
                  >
                    {customerTitleList.map((i) => (
                      <MenuItem value={i.value} key={i.value}>{i.label}</MenuItem>
                    ))}
                  </Select>
                  {getLocalAndLenderError({ fieldName: `customerTitle_${customer._id}` }) &&
                    <FormHelperText sx={{ color: "#d32f2f" }}>{getLocalAndLenderError({ fieldName: `customerTitle_${customer._id}` })}</FormHelperText>
                  }
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  id={`firstName_${customer._id}`}
                  type="text"
                  name={`firstName_${customer._id}`}
                  label="First name"
                  variant="filled"
                  size="small"
                  disabled={isDisable}
                  value={allValues?.firstName}
                  error={!!getLocalAndLenderError({ fieldName: `firstName_${customer._id}` })}
                  helperText={getLocalAndLenderError({ fieldName: `firstName_${customer._id}` })}
                  onChange={(event) => handle.onChangeField(event?.target?.value, `firstName_${customer._id}`)}
                  onBlur={(e) => handle.blurFn(`firstName_${customer._id}`, e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  id={`middleNames_${customer._id}`}
                  type="text"
                  name={`middleNames_${customer._id}`}
                  label="Other given names"
                  variant="filled"
                  size="small"
                  disabled={isDisable}
                  value={allValues?.middleNames}
                  error={!!getLocalAndLenderError({ fieldName: `middleNames_${customer._id}` })}
                  helperText={getLocalAndLenderError({ fieldName: `middleNames_${customer._id}` })}
                  onChange={(event) => handle.onChangeField(event?.target?.value, `middleNames_${customer._id}`)}
                  onBlur={(e) => handle.blurFn(`middleNames_${customer._id}`, e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  id={`lastName_${customer._id}`}
                  type="text"
                  name={`lastName_${customer._id}`}
                  label="Last name"
                  variant="filled"
                  size="small"
                  disabled={isDisable}
                  value={allValues?.lastName}
                  error={!!getLocalAndLenderError({ fieldName: `lastName_${customer._id}` })}
                  helperText={getLocalAndLenderError({ fieldName: `lastName_${customer._id}` })}
                  onChange={(event) => handle.onChangeField(event?.target?.value, `lastName_${customer._id}`)}
                  onBlur={(e) => handle.blurFn(`lastName_${customer._id}`, e.target.value)}
                />
              </Grid>
            </Stack>
            <Stack spacing={1} direction={{ xs: "column", sm: "row" }} margin={"0 0 10px"} width={"100%"}>
              <Grid item xs={12} sm={4}>
                <InputMask
                  mask="99-99-9999"
                  value={allValues?.dateOfBirth || ""}
                  disabled={isDisable || false}
                  maskChar={null}
                  onChange={(event) => {
                    handle.onChangeField(event?.target.value || null, `dateOfBirth_${customer._id}`);
                  }}
                  onBlur={(e) => handle.blurFn(`dateOfBirth_${customer._id}`, e.target.value)}
                >
                  {() => (
                    <TextField
                      label="Date of birth"
                      size="small"
                      fullWidth
                      placeholder="DD-MM-YYYY"
                      variant="filled"
                      disabled={isDisable}
                      name={`dateOfBirth_${customer._id}`}
                      error={!!getLocalAndLenderError({ fieldName: `dateOfBirth_${customer._id}` })}
                      helperText={getLocalAndLenderError({ fieldName: `dateOfBirth_${customer._id}` })}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  id={`IDNumber_${customer._id}`}
                  type="text"
                  name={`IDNumber_${customer._id}`}
                  label={allValues?.IDType === ID_TYPE.LICENCE ? 'Driver licence' : 'Passport no'}
                  variant="filled"
                  size="small"
                  disabled={isDisable}
                  value={allValues?.IDNumber}
                  error={!!getLocalAndLenderError({ fieldName: `IDNumber_${customer._id}` })}
                  helperText={getLocalAndLenderError({ fieldName: `IDNumber_${customer._id}` })}
                  onChange={(event) => handle.onChangeField(event.target.value, `IDNumber_${customer._id}`)}
                  onBlur={(e) => handle.blurFn(`IDNumber_${customer._id}`, e.target.value)}
                />
                <LicencePassportToggle allValues={allValues} setAllValue={setAllValue} handleSaveIDType={handle.handleSaveIDType} isDisable={isDisable} />
              </Grid>
              <Grid item xs={12} sm={4}>
                {allValues?.IDType === ID_TYPE.LICENCE && (
                  <TextField
                    fullWidth
                    variant="filled"
                    select
                    value={allValues?.IDState || ''}
                    name={`IDState_${customer._id}`}
                    label={`${allValues?.IDType} State`}
                    size="small"
                    disabled={isDisable}
                    onChange={(event) => handle.onChangeField(event?.target?.value, `IDState_${customer._id}`)}
                    onBlur={(event) => handle.blurFn(`IDState_${customer._id}`, event.target.value)}
                    error={!!getLocalAndLenderError({ fieldName: `IDState_${customer._id}` })}
                    helperText={getLocalAndLenderError({ fieldName: `IDState_${customer._id}` })}
                  >
                    {stateList.map((state, i) => (
                      <MenuItem value={state} key={state}>{state}</MenuItem>
                    ))}
                  </TextField>
                )}
                {allValues?.IDType === ID_TYPE.PASSPORT && (
                  <Autocomplete
                    autoComplete
                    disableClearable
                    loadingText={<AutoCompleteLoader />}
                    value={allValues?.IDCountry || ''}
                    options={countryList}
                    id={`IDCountry_${customer._id}`}
                    name={`IDCountry_${customer._id}`}
                    size="small"
                    disabled={isDisable}
                    getOptionLabel={(option) => option || ""}
                    onChange={(_event, newValue) => handle.onChangeField(newValue, `IDCountry_${customer._id}`)}
                    onBlur={(_event) => handle.blurFn(`IDCountry_${customer._id}`, allValues?.IDCountry)}
                    renderInput={(params) => (
                      <TextField
                        fullWidth
                        variant="filled"
                        label={`${allValues?.IDType} Country`}
                        size="small"
                        error={!!getLocalAndLenderError({ fieldName: `IDCountry_${customer._id}` })}
                        helperText={getLocalAndLenderError({ fieldName: `IDCountry_${customer._id}` })}
                        {...params}
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  id={`IDCardNumber_${customer._id}`}
                  type="text"
                  name={`IDCardNumber_${customer._id}`}
                  label={(allValues?.IDType === ID_TYPE.LICENCE ? ID_TYPE.LICENCE : ID_TYPE.PASSPORT) + " Card No"}
                  variant="filled"
                  size="small"
                  disabled={isDisable}
                  value={allValues?.IDCardNumber}
                  error={!!getLocalAndLenderError({ fieldName: `IDCardNumber_${customer._id}` })}
                  helperText={getLocalAndLenderError({ fieldName: `IDCardNumber_${customer._id}` })}
                  onChange={(event) => handle.onChangeField(event.target.value, `IDCardNumber_${customer._id}`)}
                  onBlur={(e) => handle.blurFn(`IDCardNumber_${customer._id}`, e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputMask
                  mask="99-99-9999"
                  value={allValues?.IDExpiryDate || ""}
                  disabled={isDisable}
                  maskChar={null}
                  onChange={(event) => {
                    handle.onChangeField(event?.target.value || null, `IDExpiryDate_${customer._id}`);
                  }}
                  onBlur={(e) => handle.blurFn(`IDExpiryDate_${customer._id}`, e.target.value)}
                >
                  {() => (
                    <TextField
                      label={(allValues?.IDType === ID_TYPE.LICENCE ? ID_TYPE.LICENCE : ID_TYPE.PASSPORT) + " Expiry Date"}
                      id="id-expiry-date-input"
                      size="small"
                      fullWidth
                      placeholder="DD-MM-YYYY"
                      variant="filled"
                      disabled={isDisable}
                      name={`IDExpiryDate_${customer._id}`}
                      error={!!getLocalAndLenderError({ fieldName: `IDExpiryDate_${customer._id}` })}
                      helperText={getLocalAndLenderError({ fieldName: `IDExpiryDate_${customer._id}` })}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </InputMask>
              </Grid>
            </Stack>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack>
                  <Typography variant="subtitle2">Residency status</Typography>
                  <RadioButtonGroup
                    required
                    name="residencyStatusValue"
                    disabled={isDisable}
                    value={allValues?.residencyStatusValue}
                    handleValue={(value) => {
                      handle.handleResidencyStatusChange(value);
                    }}
                    style={{
                      margin: "0 0 0 8px",
                      flexWrap: `${!isLargeScreen ? "wrap" : "unset"}`,
                    }}
                    optionStyle={{
                      width: `${!isLargeScreen ? "100%" : "50%"}`,
                      padding: "8px",
                      minHeight: "20px",
                      ...getLocalAndLenderError({
                        fieldName: `customerProfile.residencyStatus_${customer._id}`,
                        localFieldName: "residencyStatusValue"
                      }) && { borderColor: "#d32f2f" },
                    }}
                    options={residencyStatusOptions}
                  />
                  {getLocalAndLenderError({ fieldName: `customerProfile.residencyStatus_${customer._id}`, localFieldName: "residencyStatusValue" }) &&
                    <FormHelperText sx={{ color: "#d32f2f" }}>{getLocalAndLenderError({ fieldName: `customerProfile.residencyStatus_${customer._id}`, localFieldName: "residencyStatusValue" })}</FormHelperText>
                  }
                </Stack>
              </Grid>
              {showVisaFields && (
                <Grid container item xs={12} spacing={2} sx={{ mt: 1, mb: 2, alignItems: "flex-start" }}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      autoComplete
                      value={selectedVisaType}
                      options={selectedVisaOptions}
                      id={`visaType_${currentCustomerId}`}
                      name={`visaType_${currentCustomerId}`}
                      size="small"
                      disabled={isDisable}
                      getOptionLabel={(option) => option || ""}
                      noOptionsText="No visas available"
                      onChange={(_event, value) => handle.handleVisaType(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="filled"
                          label="Visa"
                          placeholder={selectedVisaOptions.length ? "Select visa" : "No visas available"}
                          error={!!visaTypeError}
                          helperText={visaTypeError}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputMask
                      mask="99-99-9999"
                      value={allValues?.visaExpiryDate || ""}
                      disabled={isDisable}
                      maskChar={null}
                      onChange={(event) => {
                        handle.onChangeCustomerProfileField(event?.target.value || null, "visaExpiryDate");
                      }}
                      onBlur={(event) => handle.blurCustomerProfileFn("visaExpiryDate", event.target.value)}
                    >
                      {() => (
                        <TextField
                          label="Visa Expiry Date"
                          id={`visaExpiryDate_${currentCustomerId}`}
                          size="small"
                          fullWidth
                          placeholder="DD-MM-YYYY"
                          variant="filled"
                          disabled={isDisable}
                          name={`visaExpiryDate_${currentCustomerId}`}
                          style={{ background: "#ffffff" }}
                          error={!!visaExpiryDateError}
                          helperText={visaExpiryDateError}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      )}
                    </InputMask>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Marital status</Typography>
                <RadioButtonGroup
                  required
                  disabled={isDisable}
                  value={allValues?.maritalStatusValue}
                  name={"maritalStatusValue"}
                  handleValue={(value) => {
                    handle.onChangeRadio(value, "maritalStatusValue");
                    handle.handleMaritalStatus(value);
                  }}
                  style={{
                    margin: "0 0 0 8px",
                    flexWrap: `${!isLargeScreen ? "wrap" : "unset"}`,
                  }}
                  optionStyle={{
                    ...(getLocalAndLenderError({
                      fieldName: `customerProfile.maritalStatus_${customer._id}`,
                      localFieldName: "maritalStatusValue",
                    }) && { borderColor: "#d32f2f" }),
                  }}
                  options={[
                    { icon: "House", label: MARITAL_STATUSES.MARRIED_DEFACTO },
                    { icon: "HomeWork", label: MARITAL_STATUSES.SINGLE_DIVORCED_WIDOWED },
                  ]}
                />
                {getLocalAndLenderError({ fieldName: `customerProfile.maritalStatus_${customer._id}`, localFieldName: "maritalStatusValue" }) &&
                  <FormHelperText sx={{ color: "#d32f2f" }}>
                    {getLocalAndLenderError({ fieldName: `customerProfile.maritalStatus_${customer._id}`, localFieldName: "maritalStatusValue" })}
                  </FormHelperText>
                }
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </React.Fragment>
    );
  },
);

export default CustomerPersonalDetails;
